import React, { useState, useEffect } from 'react';
import { Star, Users, Mic, ArrowLeft, Eye, EyeOff } from 'lucide-react';
import LabelConnectTab from './LabelConnectTab';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// ═══════════════════════════════════════════════════
// PANEL SELECTOR
// ═══════════════════════════════════════════════════

const PANELS = [
  { id: 'labels', name: 'Labels', subtitle: 'Record labels accepting demos', icon: Star, color: '#ff00ff' },
  { id: 'collabs', name: 'Collabs', subtitle: 'Find producers to collaborate with', icon: Users, color: '#00ffff' },
  { id: 'vocalists', name: 'Vocalists', subtitle: 'Connect with singers & MCs', icon: Mic, color: '#ff6600' },
];

// ═══════════════════════════════════════════════════
// COLLAB BUILDER (Public Feed)
// ═══════════════════════════════════════════════════

const GENRE_OPTIONS = ['House', 'Techno', 'Trance', 'DnB', 'Dubstep', 'Garage', 'Ambient', 'Lo-Fi', 'Future Bass', 'Breaks', 'Minimal', 'Progressive', 'Acid', 'Downtempo', 'Electro'];
const DAW_OPTIONS = ['Ableton Live', 'FL Studio', 'Logic Pro', 'Bitwig', 'Cubase', 'Reason', 'Pro Tools', 'Studio One', 'Reaper', 'Other'];
const ROLE_OPTIONS = ['Producer', 'Sound Designer', 'Mix Engineer', 'Mastering Engineer', 'Vocalist', 'DJ', 'Songwriter'];

const CollabBuilder = () => {
  const [briefs, setBriefs] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('all'); // 'all' or 'mine'
  const [revealedContacts, setRevealedContacts] = useState({});
  const [currentUserId, setCurrentUserId] = useState(null);
  const [newBrief, setNewBrief] = useState({
    project_name: '', bpm: 128, key: 'Am', genre: 'House', daw: 'Ableton Live',
    description: '', looking_for: [], reference_tracks: '', stems_needed: '',
    deadline: '', file_format: 'WAV 44.1kHz/24-bit', contact_info: '',
  });

  const getToken = () => localStorage.getItem('beatseek_token');

  useEffect(() => {
    loadBriefs();
    // Get current user ID from token
    const token = getToken();
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setCurrentUserId(payload.user_id || payload.sub);
      } catch (e) { /* ignore */ }
    }
  }, []);

  const loadBriefs = async (mine = false) => {
    const token = getToken();
    if (token) {
      try {
        const url = mine ? `${BACKEND_URL}/api/tools/collab-briefs?mine=true` : `${BACKEND_URL}/api/tools/collab-briefs`;
        const res = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
        if (res.ok) {
          const data = await res.json();
          setBriefs(data.briefs || []);
        }
      } catch (e) { console.error('Failed to load briefs:', e); }
    }
    setLoading(false);
  };

  const switchView = (mode) => {
    setViewMode(mode);
    setLoading(true);
    loadBriefs(mode === 'mine');
  };

  const toggleRole = (role) => {
    setNewBrief(prev => ({
      ...prev,
      looking_for: prev.looking_for.includes(role) ? prev.looking_for.filter(r => r !== role) : [...prev.looking_for, role]
    }));
  };

  const saveBrief = async () => {
    if (!newBrief.project_name.trim()) return;
    const token = getToken();
    if (!token) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/tools/collab-briefs`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(newBrief),
      });
      if (res.ok) {
        setNewBrief({ project_name: '', bpm: 128, key: 'Am', genre: 'House', daw: 'Ableton Live', description: '', looking_for: [], reference_tracks: '', stems_needed: '', deadline: '', file_format: 'WAV 44.1kHz/24-bit', contact_info: '' });
        setShowCreate(false);
        switchView('all');
      }
    } catch (e) { console.error('Failed to save brief:', e); }
  };

  const deleteBrief = async (id) => {
    const token = getToken();
    if (!token) return;
    try {
      await fetch(`${BACKEND_URL}/api/tools/collab-briefs/${id}`, {
        method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` },
      });
      setBriefs(prev => prev.filter(b => b.id !== id));
    } catch (e) { console.error('Failed to delete brief:', e); }
  };

  const updateStatus = async (id, status) => {
    const token = getToken();
    if (!token) return;
    try {
      await fetch(`${BACKEND_URL}/api/tools/collab-briefs/${id}/status`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      setBriefs(prev => prev.map(b => b.id === id ? { ...b, status } : b));
    } catch (e) { console.error('Failed to update status:', e); }
  };

  const toggleContact = (id) => {
    setRevealedContacts(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const KEYS = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B', 'Am', 'Bm', 'Cm', 'Dm', 'Em', 'Fm', 'Gm'];
  const STATUS_COLORS = { open: '#00ff88', in_progress: '#ffaa00', closed: '#ff3333' };
  const STATUS_LABELS = { open: 'Open', in_progress: 'In Progress', closed: 'Closed' };

  return (
    <div data-testid="collab-builder" style={{ padding: '1rem' }}>
      {/* View toggle + Create */}
      <div style={{ display: 'flex', gap: '6px', marginBottom: '1rem' }}>
        {[{ id: 'all', label: 'All Briefs' }, { id: 'mine', label: 'My Briefs' }].map(v => (
          <button key={v.id} onClick={() => switchView(v.id)} data-testid={`collab-view-${v.id}`} style={{
            flex: 1, padding: '9px', fontSize: '12px', fontWeight: 600, borderRadius: '8px', cursor: 'pointer', border: 'none',
            background: viewMode === v.id ? 'rgba(0,255,255,0.15)' : 'rgba(255,255,255,0.04)',
            color: viewMode === v.id ? '#00ffff' : '#888', transition: 'all 0.2s',
          }}>{v.label}</button>
        ))}
      </div>

      <button onClick={() => setShowCreate(!showCreate)} data-testid="collab-create-toggle" style={{
        width: '100%', padding: '12px', fontSize: '13px', fontWeight: 700, borderRadius: '10px', cursor: 'pointer', border: 'none',
        background: showCreate ? 'rgba(255,51,51,0.15)' : 'linear-gradient(135deg, rgba(0,255,255,0.15), rgba(0,200,255,0.15))',
        color: showCreate ? '#ff3333' : '#00ffff', marginBottom: '1.25rem', transition: 'all 0.2s',
      }}>{showCreate ? 'Cancel' : '+ Post Collab Brief'}</button>

      {/* Create form */}
      {showCreate && (
        <div style={{ padding: '16px', background: 'rgba(255,255,255,0.03)', borderRadius: '12px', marginBottom: '1.25rem', border: '1px solid rgba(0,255,255,0.1)' }}>
          <input data-testid="collab-project-name" value={newBrief.project_name} onChange={(e) => setNewBrief(p => ({ ...p, project_name: e.target.value }))} placeholder="Project name..." style={{ width: '100%', padding: '10px 12px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff', fontSize: '13px', fontWeight: 600, outline: 'none', marginBottom: '10px', boxSizing: 'border-box' }} />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', marginBottom: '10px' }}>
            {[
              { label: 'BPM', type: 'number', value: newBrief.bpm, key: 'bpm' },
            ].map(f => (
              <div key={f.key}>
                <div style={{ fontSize: '10px', fontWeight: 600, color: '#666', marginBottom: '4px' }}>{f.label}</div>
                <input type={f.type} value={f.value} onChange={(e) => setNewBrief(p => ({ ...p, [f.key]: f.type === 'number' ? parseInt(e.target.value) || 120 : e.target.value }))} style={{ width: '100%', padding: '8px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: '#fff', fontSize: '12px', outline: 'none', boxSizing: 'border-box' }} />
              </div>
            ))}
            <div>
              <div style={{ fontSize: '10px', fontWeight: 600, color: '#666', marginBottom: '4px' }}>Key</div>
              <select value={newBrief.key} onChange={(e) => setNewBrief(p => ({ ...p, key: e.target.value }))} style={{ width: '100%', padding: '8px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: '#fff', fontSize: '12px', outline: 'none' }}>
                {KEYS.map(k => <option key={k} value={k}>{k}</option>)}
              </select>
            </div>
            <div>
              <div style={{ fontSize: '10px', fontWeight: 600, color: '#666', marginBottom: '4px' }}>Genre</div>
              <select value={newBrief.genre} onChange={(e) => setNewBrief(p => ({ ...p, genre: e.target.value }))} style={{ width: '100%', padding: '8px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: '#fff', fontSize: '12px', outline: 'none' }}>
                {GENRE_OPTIONS.map(g => <option key={g} value={g}>{g}</option>)}
              </select>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '10px' }}>
            <div>
              <div style={{ fontSize: '10px', fontWeight: 600, color: '#666', marginBottom: '4px' }}>DAW</div>
              <select value={newBrief.daw} onChange={(e) => setNewBrief(p => ({ ...p, daw: e.target.value }))} style={{ width: '100%', padding: '8px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: '#fff', fontSize: '12px', outline: 'none' }}>
                {DAW_OPTIONS.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <div>
              <div style={{ fontSize: '10px', fontWeight: 600, color: '#666', marginBottom: '4px' }}>File Format</div>
              <select value={newBrief.file_format} onChange={(e) => setNewBrief(p => ({ ...p, file_format: e.target.value }))} style={{ width: '100%', padding: '8px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: '#fff', fontSize: '12px', outline: 'none' }}>
                {['WAV 44.1kHz/24-bit', 'WAV 48kHz/24-bit', 'WAV 96kHz/32-bit', 'FLAC', 'AIFF'].map(f => <option key={f} value={f}>{f}</option>)}
              </select>
            </div>
          </div>
          <div style={{ marginBottom: '10px' }}>
            <div style={{ fontSize: '10px', fontWeight: 600, color: '#666', marginBottom: '6px' }}>Looking for</div>
            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
              {ROLE_OPTIONS.map(role => (
                <button key={role} onClick={() => toggleRole(role)} style={{
                  padding: '5px 12px', fontSize: '10px', fontWeight: 600, borderRadius: '14px', cursor: 'pointer', border: 'none',
                  background: newBrief.looking_for.includes(role) ? 'rgba(0,255,255,0.2)' : 'rgba(255,255,255,0.04)',
                  color: newBrief.looking_for.includes(role) ? '#00ffff' : '#888', transition: 'all 0.2s',
                }}>{role}</button>
              ))}
            </div>
          </div>
          <textarea value={newBrief.description} onChange={(e) => setNewBrief(p => ({ ...p, description: e.target.value }))} placeholder="Describe the project, vibe, and what you're going for..." style={{ width: '100%', minHeight: '60px', padding: '8px 10px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: '#fff', fontSize: '11px', outline: 'none', resize: 'vertical', marginBottom: '8px', boxSizing: 'border-box' }} />
          <input value={newBrief.reference_tracks} onChange={(e) => setNewBrief(p => ({ ...p, reference_tracks: e.target.value }))} placeholder="Reference tracks..." style={{ width: '100%', padding: '8px 10px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: '#fff', fontSize: '11px', outline: 'none', marginBottom: '8px', boxSizing: 'border-box' }} />
          <input value={newBrief.stems_needed} onChange={(e) => setNewBrief(p => ({ ...p, stems_needed: e.target.value }))} placeholder="Stems needed..." style={{ width: '100%', padding: '8px 10px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: '#fff', fontSize: '11px', outline: 'none', marginBottom: '8px', boxSizing: 'border-box' }} />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '10px' }}>
            <input type="date" value={newBrief.deadline} onChange={(e) => setNewBrief(p => ({ ...p, deadline: e.target.value }))} style={{ padding: '8px 10px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: '#fff', fontSize: '11px', outline: 'none' }} />
            <input value={newBrief.contact_info} onChange={(e) => setNewBrief(p => ({ ...p, contact_info: e.target.value }))} placeholder="Your contact (Discord, email...)" style={{ padding: '8px 10px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: '#fff', fontSize: '11px', outline: 'none' }} />
          </div>
          <button onClick={saveBrief} data-testid="collab-save-btn" style={{ width: '100%', padding: '12px', fontSize: '13px', fontWeight: 700, borderRadius: '10px', cursor: 'pointer', border: 'none', background: 'linear-gradient(135deg, #00ffff, #00ccff)', color: '#000', boxShadow: '0 4px 16px rgba(0,255,255,0.2)' }}>Post Brief</button>
        </div>
      )}

      {/* Brief feed */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '2rem', color: '#555' }}>Loading...</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {briefs.length === 0 && (
            <div style={{ textAlign: 'center', padding: '3rem 1rem', color: '#555' }}>
              <div style={{ fontSize: '2rem', marginBottom: '8px' }}>&#129309;</div>
              <div style={{ fontSize: '13px', fontWeight: 600, color: '#888', marginBottom: '4px' }}>
                {viewMode === 'mine' ? 'You haven\'t posted any briefs yet' : 'No collab briefs yet — be the first!'}
              </div>
            </div>
          )}
          {briefs.map(brief => {
            const isOwner = brief.user_id === currentUserId;
            const status = brief.status || 'open';
            return (
              <div key={brief.id} data-testid={`collab-brief-${brief.id}`} style={{
                padding: '16px', borderRadius: '12px',
                background: 'rgba(255,255,255,0.03)',
                border: `1px solid ${isOwner ? 'rgba(0,255,255,0.15)' : 'rgba(255,255,255,0.06)'}`,
              }}>
                {/* User header */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                  <div style={{
                    width: '32px', height: '32px', borderRadius: '50%', overflow: 'hidden',
                    background: 'linear-gradient(135deg, #00ffff33, #ff00ff33)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '13px', fontWeight: 700, color: '#fff', flexShrink: 0,
                  }}>
                    {brief.user_avatar ? <img src={brief.user_avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : (brief.user_name || 'U').charAt(0).toUpperCase()}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{ fontSize: '12px', fontWeight: 700, color: '#fff' }}>{brief.user_name || 'Anonymous'}</span>
                      {brief.user_type && brief.user_type !== 'regular' && (
                        <span style={{ fontSize: '8px', padding: '2px 6px', borderRadius: '6px', background: 'rgba(0,255,255,0.1)', color: '#00cccc', fontWeight: 700, textTransform: 'uppercase' }}>{brief.user_type}</span>
                      )}
                      {isOwner && <span style={{ fontSize: '8px', padding: '2px 6px', borderRadius: '6px', background: 'rgba(255,0,255,0.1)', color: '#ff66ff', fontWeight: 700 }}>YOU</span>}
                    </div>
                    <div style={{ fontSize: '10px', color: '#555' }}>{new Date(brief.created_at).toLocaleDateString()}</div>
                  </div>
                  <span style={{ fontSize: '9px', padding: '3px 8px', borderRadius: '8px', fontWeight: 700, background: `${STATUS_COLORS[status]}15`, color: STATUS_COLORS[status] }}>{STATUS_LABELS[status]}</span>
                  {isOwner && (
                    <button onClick={() => deleteBrief(brief.id)} style={{ background: 'none', border: 'none', color: '#555', cursor: 'pointer', fontSize: '16px' }}>×</button>
                  )}
                </div>

                {/* Brief content */}
                <div style={{ fontSize: '15px', fontWeight: 700, color: '#fff', marginBottom: '6px' }}>{brief.project_name}</div>
                <div style={{ fontSize: '11px', color: '#888', marginBottom: '8px' }}>
                  {brief.genre} | {brief.bpm} BPM | {brief.key} | {brief.daw}
                </div>
                {(brief.looking_for || []).length > 0 && (
                  <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginBottom: '8px' }}>
                    {brief.looking_for.map(r => <span key={r} style={{ fontSize: '9px', padding: '3px 8px', borderRadius: '10px', background: 'rgba(0,255,255,0.1)', color: '#00cccc', fontWeight: 600 }}>{r}</span>)}
                  </div>
                )}
                {brief.description && <div style={{ fontSize: '12px', color: '#aaa', marginBottom: '6px', lineHeight: '1.5' }}>{brief.description}</div>}
                {brief.reference_tracks && <div style={{ fontSize: '10px', color: '#777', marginBottom: '4px' }}><span style={{ fontWeight: 600, color: '#888' }}>Refs:</span> {brief.reference_tracks}</div>}
                {brief.stems_needed && <div style={{ fontSize: '10px', color: '#777', marginBottom: '4px' }}><span style={{ fontWeight: 600, color: '#888' }}>Stems:</span> {brief.stems_needed}</div>}
                {brief.deadline && <div style={{ fontSize: '10px', color: '#777', marginBottom: '4px' }}><span style={{ fontWeight: 600, color: '#888' }}>Deadline:</span> {brief.deadline}</div>}

                {/* Contact reveal / owner controls */}
                {isOwner ? (
                  <div style={{ display: 'flex', gap: '6px', marginTop: '10px' }}>
                    {['open', 'in_progress', 'closed'].map(s => (
                      <button key={s} onClick={() => updateStatus(brief.id, s)} style={{
                        flex: 1, padding: '6px', fontSize: '10px', fontWeight: 600, borderRadius: '6px', cursor: 'pointer', border: 'none',
                        background: status === s ? `${STATUS_COLORS[s]}20` : 'rgba(255,255,255,0.04)',
                        color: status === s ? STATUS_COLORS[s] : '#666',
                      }}>{STATUS_LABELS[s]}</button>
                    ))}
                  </div>
                ) : brief.contact_info ? (
                  <button onClick={() => toggleContact(brief.id)} data-testid={`collab-reveal-${brief.id}`} style={{
                    width: '100%', padding: '10px', fontSize: '11px', fontWeight: 700, borderRadius: '8px', cursor: 'pointer', border: 'none', marginTop: '10px',
                    background: revealedContacts[brief.id] ? 'rgba(0,255,255,0.08)' : 'rgba(0,255,255,0.06)',
                    color: '#00ffff', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', transition: 'all 0.2s',
                  }}>
                    {revealedContacts[brief.id] ? <><EyeOff size={13} /> {brief.contact_info}</> : <><Eye size={13} /> Show Contact Info</>}
                  </button>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════
// VOCALIST CONNECT (Public Feed)
// ═══════════════════════════════════════════════════

const VOCAL_STYLES = ['Singer', 'Rapper / MC', 'Topline Writer', 'Spoken Word', 'Voice Actor', 'Choir / Harmonies'];
const VOCAL_GENRES = ['House', 'Techno', 'DnB', 'Dubstep', 'Future Bass', 'Trance', 'Garage', 'Lo-Fi', 'Pop-Electronic', 'R&B Electronic', 'Ambient', 'Hip-Hop'];

const VocalistConnect = () => {
  const [listings, setListings] = useState([]);
  const [showPost, setShowPost] = useState(false);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('all');
  const [revealedContacts, setRevealedContacts] = useState({});
  const [currentUserId, setCurrentUserId] = useState(null);
  const [newListing, setNewListing] = useState({
    title: '', type: 'seeking', vocal_style: '', genres: [],
    description: '', budget: '', contact: '', reference_link: '',
  });

  const getToken = () => localStorage.getItem('beatseek_token');

  useEffect(() => {
    loadListings();
    const token = getToken();
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setCurrentUserId(payload.user_id || payload.sub);
      } catch (e) { /* ignore */ }
    }
  }, []);

  const loadListings = async (mine = false) => {
    const token = getToken();
    if (token) {
      try {
        const url = mine ? `${BACKEND_URL}/api/tools/vocalist-listings?mine=true` : `${BACKEND_URL}/api/tools/vocalist-listings`;
        const res = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
        if (res.ok) {
          const data = await res.json();
          setListings(data.listings || []);
        }
      } catch (e) { console.error('Failed to load listings:', e); }
    }
    setLoading(false);
  };

  const switchView = (mode) => {
    setViewMode(mode);
    setLoading(true);
    loadListings(mode === 'mine');
  };

  const toggleGenre = (genre) => {
    setNewListing(prev => ({
      ...prev,
      genres: prev.genres.includes(genre) ? prev.genres.filter(g => g !== genre) : [...prev.genres, genre]
    }));
  };

  const saveListing = async () => {
    if (!newListing.title.trim()) return;
    const token = getToken();
    if (!token) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/tools/vocalist-listings`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(newListing),
      });
      if (res.ok) {
        setNewListing({ title: '', type: 'seeking', vocal_style: '', genres: [], description: '', budget: '', contact: '', reference_link: '' });
        setShowPost(false);
        switchView('all');
      }
    } catch (e) { console.error('Failed to save listing:', e); }
  };

  const deleteListing = async (id) => {
    const token = getToken();
    if (!token) return;
    try {
      await fetch(`${BACKEND_URL}/api/tools/vocalist-listings/${id}`, {
        method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` },
      });
      setListings(prev => prev.filter(l => l.id !== id));
    } catch (e) { console.error('Failed to delete listing:', e); }
  };

  const updateStatus = async (id, status) => {
    const token = getToken();
    if (!token) return;
    try {
      await fetch(`${BACKEND_URL}/api/tools/vocalist-listings/${id}/status`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      setListings(prev => prev.map(l => l.id === id ? { ...l, status } : l));
    } catch (e) { console.error('Failed to update status:', e); }
  };

  const toggleContact = (id) => {
    setRevealedContacts(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const STATUS_COLORS = { open: '#00ff88', in_progress: '#ffaa00', closed: '#ff3333' };
  const STATUS_LABELS = { open: 'Open', in_progress: 'In Progress', closed: 'Closed' };

  return (
    <div data-testid="vocalist-connect" style={{ padding: '1rem' }}>
      {/* View toggle */}
      <div style={{ display: 'flex', gap: '6px', marginBottom: '1rem' }}>
        {[{ id: 'all', label: 'All Listings' }, { id: 'mine', label: 'My Listings' }].map(v => (
          <button key={v.id} onClick={() => switchView(v.id)} data-testid={`vocalist-view-${v.id}`} style={{
            flex: 1, padding: '9px', fontSize: '12px', fontWeight: 600, borderRadius: '8px', cursor: 'pointer', border: 'none',
            background: viewMode === v.id ? 'rgba(255,102,0,0.15)' : 'rgba(255,255,255,0.04)',
            color: viewMode === v.id ? '#ff8800' : '#888', transition: 'all 0.2s',
          }}>{v.label}</button>
        ))}
      </div>

      <button onClick={() => setShowPost(!showPost)} data-testid="vocalist-create-toggle" style={{
        width: '100%', padding: '12px', fontSize: '13px', fontWeight: 700, borderRadius: '10px', cursor: 'pointer', border: 'none',
        background: showPost ? 'rgba(255,51,51,0.15)' : 'linear-gradient(135deg, rgba(255,102,0,0.15), rgba(255,150,0,0.15))',
        color: showPost ? '#ff3333' : '#ff8800', marginBottom: '1.25rem', transition: 'all 0.2s',
      }}>{showPost ? 'Cancel' : '+ Post Vocalist Listing'}</button>

      {/* Create form */}
      {showPost && (
        <div style={{ padding: '16px', background: 'rgba(255,255,255,0.03)', borderRadius: '12px', marginBottom: '1.25rem', border: '1px solid rgba(255,102,0,0.1)' }}>
          <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
            {[{ id: 'seeking', label: 'Seeking Vocalist' }, { id: 'offering', label: 'Offering Vocals' }].map(t => (
              <button key={t.id} onClick={() => setNewListing(p => ({ ...p, type: t.id }))} style={{
                flex: 1, padding: '10px', fontSize: '12px', fontWeight: 600, borderRadius: '8px', cursor: 'pointer', border: 'none',
                background: newListing.type === t.id ? 'rgba(255,102,0,0.2)' : 'rgba(255,255,255,0.04)',
                color: newListing.type === t.id ? '#ff8800' : '#888',
              }}>{t.label}</button>
            ))}
          </div>
          <input value={newListing.title} onChange={(e) => setNewListing(p => ({ ...p, title: e.target.value }))} placeholder="Listing title..." style={{ width: '100%', padding: '10px 12px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff', fontSize: '13px', fontWeight: 600, outline: 'none', marginBottom: '10px', boxSizing: 'border-box' }} />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '10px' }}>
            <div>
              <div style={{ fontSize: '10px', fontWeight: 600, color: '#666', marginBottom: '4px' }}>Vocal Style</div>
              <select value={newListing.vocal_style} onChange={(e) => setNewListing(p => ({ ...p, vocal_style: e.target.value }))} style={{ width: '100%', padding: '8px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: '#fff', fontSize: '12px', outline: 'none' }}>
                <option value="">Select style...</option>
                {VOCAL_STYLES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <div style={{ fontSize: '10px', fontWeight: 600, color: '#666', marginBottom: '4px' }}>Budget</div>
              <input value={newListing.budget} onChange={(e) => setNewListing(p => ({ ...p, budget: e.target.value }))} placeholder="e.g. $50-100, Royalty split" style={{ width: '100%', padding: '8px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: '#fff', fontSize: '12px', outline: 'none', boxSizing: 'border-box' }} />
            </div>
          </div>
          <div style={{ marginBottom: '10px' }}>
            <div style={{ fontSize: '10px', fontWeight: 600, color: '#666', marginBottom: '6px' }}>Genres</div>
            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
              {VOCAL_GENRES.map(g => (
                <button key={g} onClick={() => toggleGenre(g)} style={{
                  padding: '4px 10px', fontSize: '10px', fontWeight: 600, borderRadius: '12px', cursor: 'pointer', border: 'none',
                  background: newListing.genres.includes(g) ? 'rgba(255,102,0,0.2)' : 'rgba(255,255,255,0.04)',
                  color: newListing.genres.includes(g) ? '#ff8800' : '#888',
                }}>{g}</button>
              ))}
            </div>
          </div>
          <textarea value={newListing.description} onChange={(e) => setNewListing(p => ({ ...p, description: e.target.value }))} placeholder="Describe what you need or offer..." style={{ width: '100%', minHeight: '60px', padding: '8px 10px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: '#fff', fontSize: '11px', outline: 'none', resize: 'vertical', marginBottom: '8px', boxSizing: 'border-box' }} />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '10px' }}>
            <input value={newListing.reference_link} onChange={(e) => setNewListing(p => ({ ...p, reference_link: e.target.value }))} placeholder="Reference link (SoundCloud, etc.)" style={{ padding: '8px 10px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: '#fff', fontSize: '11px', outline: 'none' }} />
            <input value={newListing.contact} onChange={(e) => setNewListing(p => ({ ...p, contact: e.target.value }))} placeholder="Your contact (Discord, email)" style={{ padding: '8px 10px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: '#fff', fontSize: '11px', outline: 'none' }} />
          </div>
          <button onClick={saveListing} data-testid="vocalist-save-btn" style={{ width: '100%', padding: '12px', fontSize: '13px', fontWeight: 700, borderRadius: '10px', cursor: 'pointer', border: 'none', background: 'linear-gradient(135deg, #ff6600, #ff8800)', color: '#000', boxShadow: '0 4px 16px rgba(255,102,0,0.2)' }}>Post Listing</button>
        </div>
      )}

      {/* Listing feed */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '2rem', color: '#555' }}>Loading...</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {listings.length === 0 && (
            <div style={{ textAlign: 'center', padding: '3rem 1rem', color: '#555' }}>
              <div style={{ fontSize: '2rem', marginBottom: '8px' }}>&#127908;</div>
              <div style={{ fontSize: '13px', fontWeight: 600, color: '#888', marginBottom: '4px' }}>
                {viewMode === 'mine' ? 'You haven\'t posted any listings yet' : 'No vocalist listings yet — be the first!'}
              </div>
            </div>
          )}
          {listings.map(listing => {
            const isOwner = listing.user_id === currentUserId;
            const status = listing.status || 'open';
            return (
              <div key={listing.id} data-testid={`vocalist-listing-${listing.id}`} style={{
                padding: '16px', borderRadius: '12px',
                background: 'rgba(255,255,255,0.03)',
                border: `1px solid ${isOwner ? 'rgba(255,102,0,0.15)' : 'rgba(255,255,255,0.06)'}`,
              }}>
                {/* User header */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                  <div style={{
                    width: '32px', height: '32px', borderRadius: '50%', overflow: 'hidden',
                    background: 'linear-gradient(135deg, #ff660033, #ff880033)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '13px', fontWeight: 700, color: '#fff', flexShrink: 0,
                  }}>
                    {listing.user_avatar ? <img src={listing.user_avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : (listing.user_name || 'U').charAt(0).toUpperCase()}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{ fontSize: '12px', fontWeight: 700, color: '#fff' }}>{listing.user_name || 'Anonymous'}</span>
                      {listing.user_type && listing.user_type !== 'regular' && (
                        <span style={{ fontSize: '8px', padding: '2px 6px', borderRadius: '6px', background: 'rgba(255,102,0,0.1)', color: '#ff8800', fontWeight: 700, textTransform: 'uppercase' }}>{listing.user_type}</span>
                      )}
                      {isOwner && <span style={{ fontSize: '8px', padding: '2px 6px', borderRadius: '6px', background: 'rgba(255,0,255,0.1)', color: '#ff66ff', fontWeight: 700 }}>YOU</span>}
                    </div>
                    <div style={{ fontSize: '10px', color: '#555' }}>{new Date(listing.created_at).toLocaleDateString()}</div>
                  </div>
                  <span style={{
                    fontSize: '9px', padding: '3px 8px', borderRadius: '8px', fontWeight: 700,
                    background: listing.type === 'seeking' ? 'rgba(255,102,0,0.12)' : 'rgba(0,255,255,0.12)',
                    color: listing.type === 'seeking' ? '#ff8800' : '#00ffff',
                  }}>{listing.type === 'seeking' ? 'SEEKING' : 'OFFERING'}</span>
                  <span style={{ fontSize: '9px', padding: '3px 8px', borderRadius: '8px', fontWeight: 700, background: `${STATUS_COLORS[status]}15`, color: STATUS_COLORS[status] }}>{STATUS_LABELS[status]}</span>
                  {isOwner && <button onClick={() => deleteListing(listing.id)} style={{ background: 'none', border: 'none', color: '#555', cursor: 'pointer', fontSize: '16px' }}>×</button>}
                </div>

                <div style={{ fontSize: '15px', fontWeight: 700, color: '#fff', marginBottom: '6px' }}>{listing.title}</div>
                <div style={{ fontSize: '11px', color: '#888', marginBottom: '8px' }}>
                  {listing.vocal_style && <span>{listing.vocal_style}</span>}
                  {listing.budget && <span> | {listing.budget}</span>}
                </div>
                {(listing.genres || []).length > 0 && (
                  <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginBottom: '8px' }}>
                    {listing.genres.map(g => <span key={g} style={{ fontSize: '9px', padding: '2px 8px', borderRadius: '8px', background: 'rgba(255,102,0,0.08)', color: '#cc7700', fontWeight: 600 }}>{g}</span>)}
                  </div>
                )}
                {listing.description && <div style={{ fontSize: '12px', color: '#aaa', marginBottom: '6px', lineHeight: '1.5' }}>{listing.description}</div>}
                {listing.reference_link && <a href={listing.reference_link} target="_blank" rel="noopener noreferrer" style={{ fontSize: '10px', color: '#ff8800', textDecoration: 'none', display: 'block', marginBottom: '6px' }}>Listen to reference &#8599;</a>}

                {/* Contact reveal / owner controls */}
                {isOwner ? (
                  <div style={{ display: 'flex', gap: '6px', marginTop: '10px' }}>
                    {['open', 'in_progress', 'closed'].map(s => (
                      <button key={s} onClick={() => updateStatus(listing.id, s)} style={{
                        flex: 1, padding: '6px', fontSize: '10px', fontWeight: 600, borderRadius: '6px', cursor: 'pointer', border: 'none',
                        background: status === s ? `${STATUS_COLORS[s]}20` : 'rgba(255,255,255,0.04)',
                        color: status === s ? STATUS_COLORS[s] : '#666',
                      }}>{STATUS_LABELS[s]}</button>
                    ))}
                  </div>
                ) : listing.contact ? (
                  <button onClick={() => toggleContact(listing.id)} data-testid={`vocalist-reveal-${listing.id}`} style={{
                    width: '100%', padding: '10px', fontSize: '11px', fontWeight: 700, borderRadius: '8px', cursor: 'pointer', border: 'none', marginTop: '10px',
                    background: revealedContacts[listing.id] ? 'rgba(255,102,0,0.08)' : 'rgba(255,102,0,0.06)',
                    color: '#ff8800', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', transition: 'all 0.2s',
                  }}>
                    {revealedContacts[listing.id] ? <><EyeOff size={13} /> {listing.contact}</> : <><Eye size={13} /> Show Contact Info</>}
                  </button>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════
// MAIN CONNECT TAB (3-panel wrapper)
// ═══════════════════════════════════════════════════

const ConnectTab = () => {
  const [activePanel, setActivePanel] = useState(null);

  if (activePanel) {
    return (
      <div data-testid="connect-tab-active">
        <button
          onClick={() => setActivePanel(null)}
          data-testid="connect-back-btn"
          style={{
            display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px', margin: '12px 12px 0',
            fontSize: '12px', fontWeight: 600, borderRadius: '8px', cursor: 'pointer', border: 'none',
            background: 'rgba(255,255,255,0.06)', color: '#888', transition: 'all 0.2s',
          }}
        >
          <ArrowLeft size={14} /> Back to Connect
        </button>

        {activePanel === 'labels' && <LabelConnectTab />}
        {activePanel === 'collabs' && <CollabBuilder />}
        {activePanel === 'vocalists' && <VocalistConnect />}
      </div>
    );
  }

  return (
    <div data-testid="connect-tab-panels" style={{ padding: '1.25rem' }}>
      <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
        <h3 style={{ fontSize: '1.3rem', fontWeight: 800, color: '#fff', marginBottom: '4px' }}>Connect</h3>
        <p style={{ fontSize: '0.8rem', color: '#888' }}>Labels & Collaborators</p>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {PANELS.map(panel => {
          const Icon = panel.icon;
          return (
            <button
              key={panel.id}
              onClick={() => setActivePanel(panel.id)}
              data-testid={`connect-panel-${panel.id}`}
              style={{
                width: '100%', padding: '20px', textAlign: 'left', cursor: 'pointer', border: 'none',
                borderRadius: '14px', display: 'flex', alignItems: 'center', gap: '16px',
                background: `linear-gradient(135deg, ${panel.color}08, ${panel.color}04)`,
                borderLeft: `3px solid ${panel.color}`,
                transition: 'all 0.25s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = `linear-gradient(135deg, ${panel.color}15, ${panel.color}08)`;
                e.currentTarget.style.transform = 'translateX(4px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = `linear-gradient(135deg, ${panel.color}08, ${panel.color}04)`;
                e.currentTarget.style.transform = 'translateX(0)';
              }}
            >
              <div style={{
                width: '44px', height: '44px', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: `${panel.color}15`, color: panel.color,
              }}>
                <Icon size={22} />
              </div>
              <div>
                <div style={{ fontSize: '15px', fontWeight: 700, color: '#fff' }}>{panel.name}</div>
                <div style={{ fontSize: '11px', color: '#888', marginTop: '2px' }}>{panel.subtitle}</div>
              </div>
              <span style={{ marginLeft: 'auto', fontSize: '18px', color: '#555' }}>›</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default ConnectTab;
